import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  alpha,
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { parseFloatToFixed } from "../../../../extras";
import { buttonVariants } from "../../../motion/buttons";

// Icons
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MoneyIcon from "@mui/icons-material/Money";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import StorefrontIcon from "@mui/icons-material/Storefront";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LabeledIconCard from "../../../ui/cards/LabeledIconCard";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import OrderStatsReportService from "../../../../api/report/orderStatsReport";
import { formatFinance } from "../../../../utils/formatters";

// Animation variants
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const OrderStats = ({ startDate, endDate }) => {
  const theme = useTheme();

  // Define states based on exactly what the Django API returns
  const [orderStats, setOrderStats] = useState({
    total_orders: 0,
    delivery_orders: 0,
    takeout_orders: 0,
    indoor_orders: 0,
    cancelled_orders: 0,
    cancellation_rate: 0,
    cancellation_rate_comparison: "lower",
    average_order_value: 0,
    average_preparation_time_seconds: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, [loadData, startDate, endDate]);

  const loadData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      // Create params object for API calls
      const params = {
        start_date: startDate,
        end_date: endDate,
      };

      // Call the stats API
      const response = await OrderStatsReportService.getOrderStatsReport(
        params
      );

      // Set stats data directly from API response
      setOrderStats(response.data);
    } catch (err) {
      console.error("Error loading order stats data:", err);
      setError("Não foi possível carregar os dados de pedidos");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Refresh data handler
  const handleRefresh = async () => {
    if (refreshing) return;

    setRefreshing(true);
    try {
      await loadData();
    } catch (e) {
      console.error("Error refreshing order data:", e);
      setError("Não foi possível atualizar os dados de pedidos");
    } finally {
      setRefreshing(false);
    }
  };

  // Convert preparation time from seconds to minutes for display
  const prepTimeMinutes = Math.round(
    orderStats.average_preparation_time_seconds / 60
  );

  // Calculate estimated cancelled order value
  const cancelledOrderValue =
    orderStats.cancelled_orders * orderStats.average_order_value;

  // Stats items configuration - keep the colors only for the icons
  const statItems = [
    {
      title: "Pedidos",
      value: orderStats.total_orders,
      icon: <ReceiptIcon />,
      color: theme.palette.primary.main,
    },
    {
      title: "Pedidos Delivery",
      value: orderStats.delivery_orders,
      icon: <DeliveryDiningIcon />,
      color: theme.palette.info.main,
    },
    {
      title: "Pedidos Retirada",
      value: orderStats.takeout_orders,
      icon: <StorefrontIcon />,
      color: theme.palette.secondary.main,
    },
    {
      title: "Pedidos Salão",
      value: orderStats.indoor_orders,
      icon: <PointOfSaleIcon />,
      color: theme.palette.success.main,
    },
    {
      title: "Ticket Médio",
      value: formatFinance(orderStats.average_order_value),
      icon: <ReceiptIcon />,
      color: theme.palette.primary.dark,
    },
    {
      title: "Tempo de Preparo Médio",
      value: `${prepTimeMinutes}min`,
      icon: <AccessTimeIcon />,
      color: theme.palette.warning.main,
    },
    {
      title: "Divider",
      value: "CANCELAMENTOS",
      icon: null,
      color: null,
    },
    {
      title: "Pedidos Cancelados",
      value: orderStats.cancelled_orders,
      icon: <CloseIcon />,
      color: theme.palette.error.main,
    },
    {
      title: "Pedidos Cancelados %",
      value: `${parseFloatToFixed(orderStats.cancellation_rate, 1)}%`,
      icon:
        orderStats.cancellation_rate_comparison === "higher" ? (
          <ArrowUpwardIcon />
        ) : (
          <ArrowDownwardIcon />
        ),
      color:
        orderStats.cancellation_rate_comparison === "higher"
          ? theme.palette.error.main
          : theme.palette.success.main,
      trend: orderStats.cancellation_rate_comparison === "higher" ? 1 : -1,
    },
    {
      title: "Receita Cancelada",
      value: formatFinance(cancelledOrderValue),
      icon: <MoneyIcon />,
      color: theme.palette.error.main,
    },
  ];

  return (
    <Box sx={{ height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ShowChartIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>
            Pedidos
          </Typography>
        </Box>

        <motion.div whileHover="hover" whileTap="tap" variants={buttonVariants}>
          <Tooltip title="Atualizar dados">
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={loading || refreshing}
              sx={{
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </motion.div>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Carregando dados...
          </Typography>
        </Box>
      ) : error ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Grid container spacing={1}>
            {statItems.map((item, index) => {
              if (item.title === "Divider") {
                return (
                  <Grid item xs={12} key={`stat-card-divider-${index}`}>
                    <Divider sx={{ my: 2 }}>
                      <Chip label={item.value} />
                    </Divider>
                  </Grid>
                );
              }

              return (
                <Grid item xs={6} sm={6} md={4} key={`stat-card-${index}`}>
                  <motion.div
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    transition={{ delay: index * 0.05 }}
                  >
                    <LabeledIconCard
                      icon={item.icon}
                      title={item.title}
                      value={item.value}
                      color={item.color}
                    />
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default OrderStats;
